import DragableCatalogueItem from '../ui-components/catalogue-components/product-components/DragableCatalogueItem';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import toggle from '../utils/toggleArray';
import { CategoryInputCard } from '../ui-components/catalogue-components/category-components/CategoryInputCard';
import ProductInputCard, {
  CreateProductDashed,
} from '../ui-components/catalogue-components/product-components/ProductInputCard';
import CategoryHeader from '../ui-components/catalogue-components/category-components/CategoryHeader';
import ImageCropDialog from '../ui-components/catalogue-components/product-components/ImageCropDialog';
import { useCatalogue } from '../firebase-logic/useCatalogue';
import { Category } from '../types';
import useAuthState from '../global-states/useAuthState';
import { Transition } from '@headlessui/react';
import { useState, Fragment, useEffect, Dispatch } from 'react';
import CustomHeaderComponent from '../ui-components/CustomHeaderComponent';
import CustomLoadingScreen from '../ui-components/CustomLoadingScreen';
import Configurator from '../ui-components/catalogue-components/configurator-components/Configurator';
import ConfiguratorMenuDropdown from '../ui-components/catalogue-components/ConfiguratorMenuDropdown';
import { fetchAddonsImages } from '../firebase-logic/useCatalogue';
import AddCategory from '../ui-components/catalogue-components/category-components/AddCategory';
import { useTranslation } from 'react-i18next';
import CatalogueEmptyState from '../ui-components/catalogue-components/CatalogueEmptyState';
import MenuUploadModal from '../ui-components/catalogue-components/MenuUploadModal';
import { UploadStatus } from '../ui-components/catalogue-components/MenuUploadStatus';
import InlineMenuUploadStatus from '../ui-components/catalogue-components/InlineMenuUploadStatus';
import {
  uploadMenuFiles,
  subscribeToMenuUploadStatus,
  checkForActiveUploads,
} from '../firebase-logic/catalogue/menuUploadService';
import toast from 'react-hot-toast';

type CategoryContentProps = {
  categoryId: string;
  products: any;
  visibleCategories: unknown[];
  openCreateAProduct: string;
  setOpenCreateAProduct: Dispatch<any>;
  createProduct: (...args: unknown[]) => void;
  editProduct: string;
  showNetto: boolean;
  setEditProduct: Dispatch<any>;
  deleteProduct: (...args: unknown[]) => void;
  updateAProduct: (...args: unknown[]) => void;
  catalogue: any;
  setCropImage: Dispatch<any>;
  addonsImages: string[];
  setSizes: (...args: unknown[]) => void;
  deleteSizes: (...args: unknown[]) => void;
  deleteAddons: (...args: unknown[]) => void;
  addAddons: (...args: unknown[]) => void;
  updateProductVisibility: (...args: unknown[]) => void;
  addLabels: (...args: unknown[]) => void;
};

const CategoryContent = ({
  categoryId,
  products,
  visibleCategories,
  openCreateAProduct,
  setOpenCreateAProduct,
  createProduct,
  editProduct,
  showNetto,
  setEditProduct,
  deleteProduct,
  updateAProduct,
  catalogue,
  setCropImage,
  setSizes,
  deleteSizes,
  deleteAddons,
  addAddons,
  updateProductVisibility,
  addLabels,
  addonsImages,
}: CategoryContentProps) => {
  const [key, setKey] = useState(0);
  useEffect(() => {
    setKey((prevState) => prevState + 1);
  }, [catalogue]);
  return (
    <div>
      <Transition
        appear={true}
        show={visibleCategories.includes(categoryId)}
        as={Fragment}
        enter='transform transition ease-in-out duration-1000'
        enterFrom='-translate-y-4 opacity-0'
        enterTo='-translate-y-0'
      >
        <div>
          <div className='ml-8 md:ml-12'>
            {openCreateAProduct === categoryId ? ( //it shows the add new product icon or the form field to add a product
              <ProductInputCard
                setOpenCreateAProduct={setOpenCreateAProduct}
                catId={categoryId}
                createProduct={createProduct}
                showNetto={showNetto ?? false}
              />
            ) : (
              <CreateProductDashed
                onsetEditCategory={() => setOpenCreateAProduct(categoryId)}
              />
            )}
          </div>
          <Droppable droppableId={categoryId} type='product'>
            {(provided: any) => (
              <div
                className='ml-8 md:ml-12'
                style={{ minHeight: '50px' }}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                ref={provided.innerRef}
              >
                {products?.map((productId: string, index: number) => {
                  return editProduct === productId ? (
                    <ProductInputCard //Edit product
                      key={`${productId}-${key}`}
                      catId={categoryId}
                      setEditProduct={setEditProduct}
                      deleteProduct={deleteProduct}
                      updateAProduct={updateAProduct}
                      product={catalogue.products[productId]}
                      showNetto={showNetto ?? false}
                    />
                  ) : (
                    <DragableCatalogueItem
                      product={catalogue.products[productId]}
                      index={index}
                      setEditProduct={setEditProduct}
                      setCropImage={setCropImage}
                      updateAProduct={updateAProduct}
                      configurator={catalogue.configurator}
                      addonsImages={addonsImages}
                      setSizes={setSizes}
                      deleteSizes={deleteSizes}
                      deleteAddons={deleteAddons}
                      addAddons={addAddons}
                      updateProductVisibility={updateProductVisibility}
                      addLabels={addLabels}
                      key={`${productId}-${key}`}
                    />
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </Transition>
    </div>
  );
};

const CatalogueScreen = () => {
  const auth = useAuthState.getState();
  const [openConfigurator, setOpenConfigurator] = useState(
    window.innerWidth > 800
  );

  const { t } = useTranslation(['catalogue']);

  const [openCreateAProduct, setOpenCreateAProduct] = useState('');
  const [editCategory, setEditCategory] = useState(''); //it holds the ID of the category that is being updated
  const [editProduct, setEditProduct] = useState(''); //it holds the ID of the product that is being updated
  const [addonsImages, setAddonsImages] = useState([]);

  // Menu upload related states
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);
  const [currentUploadJobId, setCurrentUploadJobId] = useState<string | null>(
    null
  );

  const toggleSideBar = () => {
    setOpenConfigurator(!openConfigurator);
  };

  useEffect(() => {
    const wrapper = async () => {
      const result = await fetchAddonsImages();
      setAddonsImages(result);
    };
    wrapper();
  }, []);

  // Then in your component:
  useEffect(() => {
    const initializeUploadStatus = async () => {
      // Only check if we're not already tracking an upload
      if (!currentUploadJobId && !uploadStatus) {
        try {
          const activeJob = await checkForActiveUploads(auth.user);

          if (activeJob && activeJob.status !== 'completed') {
            // Found an active job, start tracking it
            setCurrentUploadJobId(activeJob.id);
            setUploadStatus(activeJob.status);
            setUploadProgress(activeJob.progress || 0);
            if (activeJob.errorMessage) {
              setUploadError(activeJob.errorMessage);
            }

            // Subscribe to status updates
            const unsubscribe = subscribeToMenuUploadStatus(
              auth.user,
              activeJob.id,
              (job) => {
                setUploadStatus(job.status);
                setUploadProgress(job.progress);
                if (job.errorMessage) {
                  setUploadError(job.errorMessage);
                }
              }
            );

            return () => unsubscribe();
          }
        } catch (error) {
          console.error('Error initializing upload status:', error);
        }
      }
    };

    initializeUploadStatus();
  }, [auth.user, currentUploadJobId]);

  /* cropImage is not null only when the upload image process is started.
   * image - holds the original file without being cropped
   */
  const [cropImage, setCropImage] = useState({ image: null, productId: '' });
  const [
    catalogue,
    visibleCategories,
    setVisibleCategories,
    onDragEnded,
    updateCategory,
    updateAProduct,
    deleteProduct,
    createCategory,
    deleteCategory,
    loading,
    deleteSizes,
    deleteAddons,
    addAddons,
    setSizes,
    updateCategoryVisibility,
    createProduct,
    updateProductVisibility,
    addLabels,
    refreshCatalogue,
  ] = useCatalogue(auth.user, auth.showNetto);

  // Handler for opening the upload menu modal
  const handleUploadMenu = () => {
    setIsUploadModalOpen(true);
  };

  // Handler for adding products manually
  const handleAddManually = () => {
    // Create an empty category if none exists
    if (!catalogue?.categoriesOrder?.length) {
      // Create a default category named
      const defaultCategory: Category = {
        id: 'default',
        name: t('default_category_name.title', 'Fresh Salads'),
        products: [],
        visible: true,
        emoji: '🥗',
      };
      createCategory(defaultCategory);
    }
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const handleRetryUpload = () => {
    setUploadStatus(null);
    setUploadProgress(0);
    setUploadError(undefined);
    setCurrentUploadJobId(null);
    setIsUploadModalOpen(true);
  };

  const handleContinueAfterUpload = () => {
    setUploadStatus(null);
    setUploadProgress(0);
    setUploadError(undefined);
    setCurrentUploadJobId(null);
    // Show loading toast
    const loadingToast = toast.loading(
      t('catalogue.refreshing', 'Refreshing catalogue...')
    );

    // Refresh catalog data
    refreshCatalogue();

    // Dismiss loading toast and show success
    toast.dismiss(loadingToast);
    toast.success(
      t('catalogue.refreshed', 'Catalogue refreshed successfully!')
    );
  };
  const handleUploadFiles = async (files: File[]) => {
    try {
      setIsUploadModalOpen(false);
      setUploadStatus('uploading');
      setUploadProgress(0);
      setUploadError(undefined);

      // Upload files to Firebase Storage
      const jobId = await uploadMenuFiles(auth.user, files);
      setCurrentUploadJobId(jobId);

      // Subscribe to status updates
      const unsubscribe = subscribeToMenuUploadStatus(
        auth.user,
        jobId,
        (job) => {
          setUploadStatus(job.status);
          setUploadProgress(job.progress);
          if (job.errorMessage) {
            setUploadError(job.errorMessage);
          }

          // Show success toast when the process is complete
          if (job.status === 'completed') {
            toast.success(
              t(
                'menu_upload.success',
                'Your menu has been successfully processed!'
              )
            );
          }
        }
      );

      // Clean up the subscription when the component unmounts
      return () => {
        unsubscribe();
      };
    } catch (error: any) {
      console.error('Error uploading menu:', error);
      setUploadStatus('failed');
      setUploadError(error.message);
      toast.error(
        t('menu_upload.error', 'Failed to upload menu. Please try again.')
      );
    }
  };

  if (loading) return <CustomLoadingScreen />;

  // Check if the catalogue is empty (no categories)
  const isCatalogueEmpty = !catalogue?.categoriesOrder?.length;

  return (
    <div>
      <CustomHeaderComponent heading={t('catalogue.label')} />

      {/* Show upload status when a file is being processed */}
      {uploadStatus && (
        <div className='container mx-auto w-full h-full lg:w-2/3 mt-8 md:mt-1o'>
          <InlineMenuUploadStatus
            status={uploadStatus}
            progress={uploadProgress}
            errorMessage={uploadError}
            onRetry={handleRetryUpload}
            onContinue={handleContinueAfterUpload}
          />
        </div>
      )}

      {/* Show empty state when there are no categories */}
      {isCatalogueEmpty && !uploadStatus ? (
        <CatalogueEmptyState
          onUploadMenu={handleUploadMenu}
          onAddManually={handleAddManually}
        />
      ) : (
        /* Show the catalogue content when there are categories or an upload is in progress */
        <div className='flex flex-column'>
          <DragDropContext onDragEnd={onDragEnded}>
            <>
              <div className='w-full lg:w-2/3'>
                {!uploadStatus && (
                  <AddCategory createCategory={createCategory} />
                )}

                {/* The biggest droppable, containing all category headers and their contents (if expanded).
                 *Helps to re-order categories
                 */}
                <Droppable droppableId='categories' type='cat'>
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {catalogue?.categoriesOrder?.map(
                        (catId: string, index: number) => {
                          const category = catalogue.categories[catId];
                          return (
                            <Draggable
                              draggableId={category.id}
                              index={index}
                              key={category.id}
                            >
                              {(
                                provided //category draggable
                              ) => (
                                <div
                                  {...provided.draggableProps}
                                  ref={provided.innerRef}
                                >
                                  {showCategoryHeader(category, provided)}
                                  {visibleCategories.includes(category.id) && (
                                    // categoryContent(category.id, category.products)}
                                    <CategoryContent
                                      categoryId={category.id}
                                      products={category.products}
                                      visibleCategories={visibleCategories}
                                      openCreateAProduct={openCreateAProduct}
                                      setOpenCreateAProduct={
                                        setOpenCreateAProduct
                                      }
                                      createProduct={createProduct}
                                      editProduct={editProduct}
                                      showNetto={auth.showNetto}
                                      setEditProduct={setEditProduct}
                                      deleteProduct={deleteProduct}
                                      updateAProduct={updateAProduct}
                                      catalogue={catalogue}
                                      setCropImage={setCropImage}
                                      addonsImages={addonsImages}
                                      setSizes={setSizes}
                                      deleteSizes={deleteSizes}
                                      deleteAddons={deleteAddons}
                                      addAddons={addAddons}
                                      updateProductVisibility={
                                        updateProductVisibility
                                      }
                                      addLabels={addLabels}
                                    />
                                  )}
                                </div>
                              )}
                            </Draggable>
                          );
                        }
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>

              <div className='hidden sm:inline-flex sm:w-1/3'>
                {/* if not uploadStatus then render ConfiguratorMenuDropdown and Configurator */}

                {!openConfigurator && !uploadStatus && (
                  <ConfiguratorMenuDropdown
                    onToggleConfigurator={toggleSideBar}
                    onUploadMenu={handleUploadMenu}
                  />
                )}
                <>
                  {!uploadStatus && (
                    <Configurator
                      openConfigurator={openConfigurator}
                      toggleSideBar={toggleSideBar}
                      onDragEnded={onDragEnded}
                      addAddons={addAddons}
                      deleteSizes={deleteSizes}
                      setSizes={setSizes}
                      deleteAddons={deleteAddons}
                      catalogue={catalogue}
                      addonsImages={addonsImages}
                    />
                  )}
                </>
              </div>
            </>
          </DragDropContext>

          {/* It opens image cropper only if the user has started the uploading process */}
          {cropImage.image !== null && (
            <ImageCropDialog
              imageUrl={URL.createObjectURL(cropImage.image)}
              cropInit={{ x: 0, y: 0 }}
              zoomInit={1}
              aspectInit={1 / 1.5}
              onCancel={() => setCropImage({ image: null, productId: '' })}
              setCropImage={setCropImage}
              productId={cropImage.productId}
              updateAProduct={updateAProduct}
              mobileView={window.innerWidth < 500 ? true : false}
            />
          )}
        </div>
      )}

      {/* Menu Upload Modal - only for file selection */}
      <MenuUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        onUpload={handleUploadFiles}
      />
    </div>
  );

  //it could be separate components but too many props needed to be given to the component
  function showCategoryHeader(category: Category, provided: any) {
    return (
      <>
        {editCategory === category.id ? (
          <CategoryInputCard //edit category form
            category={category}
            updateCategory={updateCategory}
            deleteCategory={deleteCategory}
            setEditCategory={setEditCategory}
            onCancelClicked={() => setEditCategory('')}
          />
        ) : (
          <CategoryHeader
            updateCategoryVisibility={updateCategoryVisibility}
            provided={provided}
            category={category}
            expanded={visibleCategories.includes(category.id)}
            setEditCategory={setEditCategory}
            onHeaderClicked={() => {
              setVisibleCategories(toggle(visibleCategories, category.id));
            }}
          />
        )}
      </>
    );
  }
};

export default CatalogueScreen;
